import { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import axios from "axios";
import {
  FaUsers,
  FaFolderOpen,
  FaLayerGroup,
  FaSpinner,
  FaRegClock,
} from "react-icons/fa";

const BASE_URL = process.env.REACT_APP_BE_MAIN_URL;

export default function SharedWithMe() {
  const token = sessionStorage.getItem("userToken");
  const username = sessionStorage.getItem("email");
  const history = useHistory();

  const [spaces, setSpaces] = useState([]);
  const [folders, setFolders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchShared = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${BASE_URL}/ocr/shared-with-me/`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setSpaces(res.data.spaces || []);
        setFolders(res.data.folders || []);
      } catch (err) {
        setError(
          err.response?.data?.error || "Could not load items shared with you."
        );
      } finally {
        setLoading(false);
      }
    };
    fetchShared();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openSpace = (space) => {
    history.push("/createfolder", {
      state: { space_path: space.space_path, username },
    });
  };

  const openFolder = (folder) => {
    history.push("/workspace", {
      state: {
        folder_name: folder.folder_name,
        space_path: folder.space_path,
        username,
      },
    });
  };

  const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString() : "");

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <FaSpinner className="animate-spin text-gray-400 w-6 h-6" />
      </div>
    );
  }

  const isEmpty = spaces.length === 0 && folders.length === 0;

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="flex items-center gap-3 mb-6">
        <FaUsers className="w-6 h-6 text-green-700" />
        <h1 className="text-2xl font-semibold text-gray-900">Shared with me</h1>
      </div>

      {error && (
        <p className="text-sm text-red-600 bg-red-50 px-3 py-2 rounded-lg mb-4">
          {error}
        </p>
      )}

      {isEmpty && !error && (
        <div className="text-center p-12">
          <FaUsers className="w-12 h-12 text-gray-300 mx-auto" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">
            Nothing has been shared with you yet.
          </h3>
        </div>
      )}

      {/* Spaces shared with you */}
      {spaces.length > 0 && (
        <section className="mb-8">
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Spaces shared with you
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {spaces.map((space) => (
              <button
                key={space.space_path_id}
                onClick={() => openSpace(space)}
                className="text-left bg-white p-5 rounded-lg shadow hover:shadow-md transition"
              >
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-blue-100 rounded-lg flex-shrink-0">
                    <FaLayerGroup className="w-5 h-5 text-blue-700" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {space.space_path}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1">
                      {space.folder_count} project
                      {space.folder_count === 1 ? "" : "s"}
                    </p>
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <FaRegClock className="w-3 h-3" />
                      Shared by {space.granted_by || "?"} ·{" "}
                      {fmtDate(space.granted_at)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}

      {/* Projects shared with you */}
      {folders.length > 0 && (
        <section>
          <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
            Projects shared with you
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {folders.map((folder) => (
              <button
                key={folder.folder_id}
                onClick={() => openFolder(folder)}
                className="text-left bg-white p-5 rounded-lg shadow hover:shadow-md transition"
              >
                <div className="flex items-start gap-3">
                  <div className="p-3 bg-green-100 rounded-lg flex-shrink-0">
                    <FaFolderOpen className="w-5 h-5 text-green-700" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="font-semibold text-gray-900 truncate">
                      {folder.folder_name}
                    </h3>
                    <p className="text-xs text-gray-500 mt-1 truncate">
                      {folder.space_path} · {folder.file_count} file
                      {folder.file_count === 1 ? "" : "s"}
                    </p>
                    <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                      <FaRegClock className="w-3 h-3" />
                      Shared by {folder.granted_by || "?"} ·{" "}
                      {fmtDate(folder.granted_at)}
                    </p>
                  </div>
                </div>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
